/**
 * All money in this system is an integer count of cents. There are no floats in
 * any stored or transmitted price. Feet and strand counts may be fractional, so
 * every multiplication against a rate has to be rounded explicitly.
 */
export type Cents = number;

/**
 * Rounds half-up to a whole cent.
 *
 * `Math.round` is half-up already, but binary floating point means a value that
 * is mathematically x.5 can arrive as x.4999999999. 8.325 * 100 evaluates to
 * 832.4999999999999, which `Math.round` sends *down* to 832 when the intended
 * answer is 833. Normalising to 6 decimal places first removes that artifact
 * while staying far below a cent of real precision.
 *
 * Inputs here are always non-negative (footage, counts, rates), so "half-up"
 * and "half away from zero" coincide. Negative input would round toward +inf.
 */
export function roundHalfUpCents(value: number): Cents {
  return Math.floor(Number(value.toFixed(6)) + 0.5);
}

/** Formats cents for display, e.g. 108000 -> "$1,080.00". */
export function formatCents(cents: Cents): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  const dollars = Math.floor(abs / 100).toLocaleString('en-US');
  const remainder = String(abs % 100).padStart(2, '0');
  return `${sign}$${dollars}.${remainder}`;
}
